export default class Crypto {
    static fromPEM(str:string) {
        const match = str.match(/\-{5}BEGIN.+?\-{5}\s([\w\d\s\+\/\,\=]+)\-{5}END.+\-{5}/)
        if (!match) throw "Invalid public key"
        return this.fromBase64(match[1])
    }

    static async toPEM(isPrivate:boolean, key:CryptoKey) {
        const exported = await window.crypto.subtle.exportKey(isPrivate ? "pkcs8" : "spki", key)
        const str = this.ab2str(exported)
        const type = isPrivate ? "PRIVATE" : "PUBLIC"
        return `-----BEGIN ${type} KEY-----\n${window.btoa(str)}\n-----END ${type} KEY-----`
    }

    static async sha256(text:string) {
        return this.toBase64(await window.crypto.subtle.digest("SHA-256", this.str2ab(text)))
    }
    static str2ab(text:string){
        return new TextEncoder().encode(text)
    }
    static ab2str(ab:ArrayBuffer) {
        return String.fromCharCode(...Array.from(new Uint8Array(ab)));
    }

    static toBase64(text:string|ArrayBuffer){
        if (typeof(text)=="string") return window.btoa(text)
        else return window.btoa(this.ab2str(text))
    }
    static fromBase64(text:string) {
        const str = window.atob(text), len = str.length;
        const buffer = new ArrayBuffer(len)
        const ab = new Uint8Array(buffer)
        for(let i=0; i<len; i++) {
            ab[i] = str.charCodeAt(i)
        }
        return ab
    }

    static async generateKey(){
        const key = await window.crypto.subtle.generateKey({name:"RSA-OAEP", modulusLength:2048, publicExponent:new Uint8Array([1,0,1]), hash:"SHA-256"}, true, ["encrypt","decrypt"])
        const publicKey = await this.toPEM(false, key.publicKey)
        const privateKey = await this.toPEM(true, key.privateKey)
        return {privateKey, publicKey}
    }
}
export class AES {
    private _key?:CryptoKey
    
    async init(password:string) {
        const hash = await crypto.subtle.digest("SHA-256", Crypto.str2ab(password))
        this._key = await crypto.subtle.importKey("raw", hash, {name:"PBKDF2"}, false, ["deriveBits"])
        return this
    }

    async encrypt(text:string){
        if (!this._key) throw "AES not initialized"
        const salt = crypto.getRandomValues(new Uint8Array(32))
        const iv = crypto.getRandomValues(new Uint8Array(12))
        const key = await this._getKey(salt.buffer, "encrypt")
        const enc = new Uint8Array(await crypto.subtle.encrypt({name:"AES-GCM", iv}, key, Crypto.str2ab(text)))
        return Crypto.toBase64(enc.buffer) + "." + Crypto.toBase64(salt.buffer) + "." + Crypto.toBase64(iv.buffer)
    }

    async decrypt(encrypted:string){
        if (!this._key) throw "AES not initialized"
        const [enc,salt,iv] = encrypted.split(".")
        const key = await this._getKey(Crypto.fromBase64(salt).buffer, "decrypt")
        const b64 = Crypto.fromBase64(enc)
        return Crypto.ab2str(await crypto.subtle.decrypt({name:"AES-GCM", iv:Crypto.fromBase64(iv)}, key, Crypto.fromBase64(enc)))
    }

    private async _getKey(salt:ArrayBuffer, func:"encrypt"|"decrypt"){
        if (!this._key) throw "AES not initialized"
        const bits = await crypto.subtle.deriveBits({
            "name": "PBKDF2",
            "salt": salt,
            "iterations": 500000,
            "hash": {
                "name": "SHA-256"
            }
        }, this._key, 256);

        return crypto.subtle.importKey("raw", bits, {name:"AES-GCM"}, false, [func])
    }
}

export class RSA {
    private _pub?:CryptoKey
    private _pri?:CryptoKey

    async init(publicKey:string,privateKey?:string){
        if (this._pub) return this

        this._pub = await crypto.subtle.importKey("spki", Crypto.fromPEM(publicKey), {
            "name":"RSA-OAEP",
            "hash":"SHA-256"
        }, true, ["encrypt"])
        
        if (privateKey) this._pri = await crypto.subtle.importKey("pkcs8", Crypto.fromPEM(privateKey), {
            name:"RSA-OAEP",
            hash:"SHA-256",
        }, true, ["decrypt"])
        return this
    }

    async encrypt(text:string) {
        const buffer = new TextEncoder().encode(text)
        const encrypted = await window.crypto.subtle.encrypt({name:"RSA-OAEP"}, this._pub!, buffer)
        return Crypto.toBase64(encrypted)
    }

    async decrypt(text:string) {
        const buffer = Crypto.fromBase64(text)
        const decrypted = await window.crypto.subtle.decrypt({name:"RSA-OAEP"}, this._pri!, buffer)
        return Crypto.ab2str(decrypted)
    }

}